import type { IDictionaryService } from 'components/dictionaries';
import { AbstractDataProviderV2, IDataProviderV2, IItem } from 'components/system/data-provider';
import { A9Service } from 'components/system/angular/A9Service';
import { RsDefaultSubTypeInfo } from 'common/customers/services/types/RsDefaultSubTypeInfo';
import { DefaultSubTypeUpdate } from 'common/customers/services/types/DefaultSubTypeUpdate';
import { ICustomerDTO } from 'common/customers/wizard/create-customer-wizard/customer-wizard.data-provider';

// also refer to dictionary customerSubTypeDefaultArea

export const AREA_ID_COMPETITION = 'Competition';
export const AREA_ID_CUSTOMER = 'Customer';
export const AREA_ID_POS = 'POS';
export const AREA_ID_RESERVATION = 'Reservation';

type TAreaId = 'Competition' | 'Customer' | 'POS' | 'Reservation';

export function getSubTypeIdDefaultAreaIds(info: IDefaultSubTypeInfo, subTypeId: number): TAreaId[] {
    const ret = [];
    if (info.competitionsSubTypeId === subTypeId) {
        ret.push(AREA_ID_COMPETITION);
    }
    if (info.customersSubTypeId === subTypeId) {
        ret.push(AREA_ID_CUSTOMER);
    }
    if (info.posSubTypeId === subTypeId) {
        ret.push(AREA_ID_POS);
    }
    if (info.reservationsSubTypeId === subTypeId) {
        ret.push(AREA_ID_RESERVATION);
    }
    return ret;
}

export function setSubTypeIdDefaultArea(info: IDefaultSubTypeInfo, subTypeId: number, areaId: TAreaId): IDefaultSubTypeInfo {
    switch (areaId) {
        case AREA_ID_COMPETITION:
            info.competitionsSubTypeId = subTypeId;
            break;
        case AREA_ID_CUSTOMER:
            info.customersSubTypeId = subTypeId;
            break;
        case AREA_ID_POS:
            info.posSubTypeId = subTypeId;
            break;
        case AREA_ID_RESERVATION:
            info.reservationsSubTypeId = subTypeId;
            break;
    }
    return info;
}

export interface IDefaultSubTypeInfo extends IItem {
    competitionsWithIdOtherClubSubTypeId: number;
    competitionsWithoutIdLocalClubSubTypeId: number;
    competitionsWithoutIdOtherClubSubTypeId: number;
    competitionsSubTypeId: number;
    customersSubTypeId: number;
    customersWithIdOtherClubSubTypeId: number;
    customersWithoutIdLocalClubSubTypeId: number;
    customersWithoutIdOtherClubSubTypeId: number;
    id: number;
    posSubTypeId: number;
    reservationsSubTypeId: number;
}

export interface ICustomerDefaultSubTypesCachedDataProvider extends IDataProviderV2<{}, RsDefaultSubTypeInfo, {}, DefaultSubTypeUpdate> {
    getForArea(areaId: TAreaId): Promise<number>;

    getForFfgArea(areaId: TAreaId, customer: ICustomerDTO, localClubId: number): Promise<number>;

    updateSingleArea(subTypeId: number, areaId: TAreaId): Promise<DefaultSubTypeUpdate>;
}

@A9Service('customerDefaultSubTypesDataProvider')
export class CustomerDefaultSubTypesDataProvider extends AbstractDataProviderV2<{}, RsDefaultSubTypeInfo, {}, DefaultSubTypeUpdate> {
    constructor(dictionaryService: IDictionaryService) {
        super();
        this.setApiUrl('middle://rs/customers/dict/sub-types/defaults');
        dictionaryService.registerLocalDictionary('customerSubTypeDefaultArea', [
            AREA_ID_COMPETITION,
            AREA_ID_CUSTOMER,
            AREA_ID_POS,
            AREA_ID_RESERVATION
        ]);
    }

    public list() {
        return Promise.reject('Method `list` is not available');
    }

    public add() {
        return Promise.reject('Method `add` is not available');
    }

    public patch() {
        return Promise.reject('Method `patch` is not available');
    }

    public remove() {
        return Promise.reject('Method `remove` is not available');
    }
}

@A9Service('customerDefaultSubTypesCachedDataProvider')
export class CustomerDefaultSubTypesCachedDataProvider
    extends AbstractDataProviderV2<{}, RsDefaultSubTypeInfo, {}, DefaultSubTypeUpdate>
    implements ICustomerDefaultSubTypesCachedDataProvider
{
    private customerDefaultSubTypesDataProvider: CustomerDefaultSubTypesDataProvider = null;

    private loadPromise: Promise<IDefaultSubTypeInfo> = null;

    constructor(customerDefaultSubTypesDataProvider) {
        super();
        this.customerDefaultSubTypesDataProvider = customerDefaultSubTypesDataProvider;
    }

    public get(): Promise<IDefaultSubTypeInfo> {
        this.loadPromise = this.customerDefaultSubTypesDataProvider.get();
        return this.loadPromise;
    }

    public getForArea(areaId: TAreaId) {
        return this.get().then(info => {
            switch (areaId) {
                case AREA_ID_COMPETITION:
                    return info.competitionsSubTypeId;
                case AREA_ID_CUSTOMER:
                    return info.customersSubTypeId;
                case AREA_ID_POS:
                    return info.posSubTypeId;
                case AREA_ID_RESERVATION:
                    return info.reservationsSubTypeId;
                default:
                    return 0;
            }
        });
    }

    public getForFfgArea(areaId: TAreaId, customer: ICustomerDTO, localClubId: number): Promise<number> {
        return this.get().then(info => {
            switch (areaId) {
                case AREA_ID_COMPETITION:
                    if (customer.federationId && customer.clubId === localClubId) {
                        return info.competitionsSubTypeId;
                    }
                    if (customer.federationId && customer.clubId !== localClubId) {
                        return info.competitionsWithIdOtherClubSubTypeId;
                    }
                    if (!customer.federationId && customer.clubId === localClubId) {
                        return info.competitionsWithoutIdLocalClubSubTypeId;
                    }
                    if (!customer.federationId && customer.clubId !== localClubId) {
                        return info.competitionsWithoutIdOtherClubSubTypeId;
                    }
                    return 0;
                case AREA_ID_CUSTOMER:
                    if (customer.federationId && customer.clubId === localClubId) {
                        return info.customersSubTypeId;
                    }
                    if (customer.federationId && customer.clubId !== localClubId) {
                        return info.customersWithIdOtherClubSubTypeId;
                    }
                    if (!customer.federationId && customer.clubId === localClubId) {
                        return info.customersWithoutIdLocalClubSubTypeId;
                    }
                    if (!customer.federationId && customer.clubId !== localClubId) {
                        return info.customersWithoutIdOtherClubSubTypeId;
                    }
                    return 0;
                case AREA_ID_POS:
                    return info.posSubTypeId;
                case AREA_ID_RESERVATION:
                    return info.reservationsSubTypeId;
                default:
                    return 0;
            }
        });
    }

    public updateSingleArea(subTypeId: number, areaId: TAreaId): Promise<DefaultSubTypeUpdate> {
        return this.get().then(info => {
            info = setSubTypeIdDefaultArea(info, subTypeId, areaId);
            return this.update(info);
        });
    }

    public update(item: DefaultSubTypeUpdate): Promise<RsDefaultSubTypeInfo> {
        return this.customerDefaultSubTypesDataProvider.update(item).then(updatedItem => {
            this.loadPromise = Promise.resolve(updatedItem); // VERY HACK, MUCH OPTIMIZEISHEN
            this.emit('update');
            return updatedItem;
        });
    }

    public onUpdate(kolbek) {
        return this.on('update', kolbek);
    }
}
